import React from "react";
import { useHistory } from "react-router-dom";
import {
  useMappedState,
  useDispatch
} from "redux-react-hook";
import { toast } from "react-toastify";

import {
  ERPRJL,
  ERPSeedless
} from "src/utils/constants";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { IAction } from "src/library/components/Button/ActionButton";
import { IStore } from "src/core/shared/store";
import useTerminology from "src/core/shared/hooks/useTerminology";
import { Text } from "src/library/components/Text";
import { useRolesApi } from "src/core/shared/api/roles.api";
import {
  CustomerDropStatuses,
  useCustomerApi
} from "src/core/customers/api/customers.api";
import { setCustomer } from "src/core/customers/store/customers.actions";
import {
  setGlobalRole,
  setMasqueradeMode
} from "src/core/shared/store/global/global.actions";
import { useCurrentUser } from "src/core/users/hooks";
import { setCurrentOrder } from "src/core/orders/store/orders.actions";
import { setCurrentList } from "src/core/lists/store/lists.actions";

import {
  IDrop,
  useDropsApi
} from "../api/drops.api";
import { setDrop } from "../store/drops.actions";

/**
 * Can be used with a table to provide state management for which actions should be shown
 */
export const useDropActions = () => {
  const history = useHistory();
  const [ dropResponse, dropRequest ] = useDropsApi("GET_DROP");
  const [ customerResponse, getCustomer ] = useCustomerApi("GET_CUSTOMER");
  const [ masqueradeRoleResponse, getMasqueradeRole ] = useRolesApi("GET_MASQUERADE_ROLE");
  const [ updateDropStatusResponse, updateDropStatus ] = useDropsApi("UPDATE_DROP_STATUS");
  const [ deactivateModalState, setDeactivateModalState ] = React.useState<IDrop & { newStatus: CustomerDropStatuses.Deactivated | CustomerDropStatuses.Suspended } | null>(null);

  const { erp, supportTicketsEnabled } = useMappedState(({ global }: IStore) => ({
    erp: global.settings && global.settings.erp.name,
    supportTicketsEnabled: global.settings?.supportSettings?.supportTickets.enabled
  }));

  const checkPermission = useCheckUserPermission();
  const terminology = useTerminology();
  const dispatch = useDispatch();
  const [ { currentRole } ] = useCurrentUser();

  /**
   * Handle update drop status api response
   */
  React.useEffect(() => {
    if (updateDropStatusResponse.data) {
      toast.success(updateDropStatusResponse.data.message);
    } else if (updateDropStatusResponse.error) {
      toast.error(updateDropStatusResponse.error.message);
    }
  }, [ updateDropStatusResponse.data, updateDropStatusResponse.error ]);

  React.useEffect(() => {
    if (customerResponse.data && dropResponse.data && masqueradeRoleResponse.data) {
      // Set masquerade mode, customer, drop and role in redux before navigating to the customer dashboard
      // to ensure correct behaviour once masquerading
      dispatch(setMasqueradeMode({
        active: true,
        redirect: history.location.pathname,
        initialRole: currentRole
      }));
      dispatch(setCurrentOrder(null));
      dispatch(setCurrentList(null));
      dispatch(setCustomer(customerResponse.data));
      dispatch(setDrop(dropResponse.data));
      dispatch(setGlobalRole(masqueradeRoleResponse.data));
      history.push("/dashboard");
    }

    if (customerResponse.error || dropResponse.error || masqueradeRoleResponse.error) {
      toast.error("Failed to enter masquerade mode");
    }
  }, [
    currentRole,
    customerResponse.data,
    customerResponse.error,
    dispatch,
    dropResponse.data,
    dropResponse.error,
    history,
    masqueradeRoleResponse.data,
    masqueradeRoleResponse.error
  ]);

  const getDropActions = React.useCallback((drop: IDrop) => {
    const canAmendDrop = checkPermission("amend-branch");

    const actions: IAction[] = [
      {
        title: `${(erp === ERPSeedless || erp === ERPRJL) && canAmendDrop ? "Edit" : "View"}`,
        action: () => {
          history.push(`/admin/customers/${drop.customerId}/drops/${drop.id}/contact-details`);
        },
        newTabPath: `/admin/customers/${drop.customerId}/drops/${drop.id}/contact-details`
      },
      {
        title: `View ${terminology?.order.plural.text}`,
        action: () => {
          history.push(`/admin/customers/${drop.customerId}/drops/${drop.id}/orders`);
        },
        newTabPath: `/admin/customers/${drop.customerId}/drops/${drop.id}/orders`
      },
      {
        title: `View ${terminology?.list.plural.text}`,
        action: () => {
          history.push(`/admin/customers/${drop.customerId}/drops/${drop.id}/lists`);
        },
        newTabPath: `/admin/customers/${drop.customerId}/drops/${drop.id}/lists`
      },
      {
        title: `Masquerade as ${terminology?.drop.singular.text}`,
        action: () => {
          dropRequest(drop.id);
          getCustomer(drop.customerId);
          getMasqueradeRole();
        }
      }
    ];

    if (supportTicketsEnabled) {
      actions.push({
        title: "View support tickets",
        action: () => {
          history.push(`/admin/customers/${drop.customerId}/drops/${drop.id}/support/tickets`);
        },
        newTabPath: `/admin/customers/${drop.customerId}/drops/${drop.id}/support-tickets`
      });
    }

    if ((erp === ERPSeedless || erp === ERPRJL) && canAmendDrop) {
      if (drop.status === CustomerDropStatuses.Active) {
        actions.push({
          title: "Suspend",
          action: () => {
            setDeactivateModalState({
              ...drop,
              newStatus: CustomerDropStatuses.Suspended
            });
          }
        });
      }

      if (drop.status !== CustomerDropStatuses.Active) {
        actions.push({
          title: "Activate",
          action: () => {
            // Update the customer
            updateDropStatus({
              cancelOrders: false,
              branchId: drop.id,
              status: CustomerDropStatuses.Active
            });
          }
        });
      }

      if ([
        CustomerDropStatuses.Active,
        CustomerDropStatuses.OverCreditLimit,
        CustomerDropStatuses.Suspended
      ].includes(drop.status)) {
        actions.push({
          // eslint-disable-next-line react/jsx-one-expression-per-line
          title: (<Text color="error">Deactivate</Text>),
          action: () => {
            setDeactivateModalState({
              ...drop,
              newStatus: CustomerDropStatuses.Deactivated
            });
          }
        });
      }
    }

    return actions;
  }, [
    checkPermission,
    dropRequest,
    erp,
    getCustomer,
    getMasqueradeRole,
    history,
    terminology?.drop.singular.text,
    terminology?.list.plural.text,
    terminology?.order.plural.text,
    updateDropStatus
  ]);

  return {
    getDropActions,
    isLoading: dropResponse.pending || customerResponse.pending || masqueradeRoleResponse.pending || updateDropStatusResponse.pending,
    deactivateModalState,
    setDeactivateModalState
  };
};
